import React from 'react'
import { useSelector, useDispatch } from 'react-redux'
import PageHeader from '@/components/PageHeader'
import { WithRouter } from '@/components/WithRouter'

import { objToSearch } from '@/utils/helpers'
import { Card, Row, Col, Pagination, Spin, Avatar, Empty, Anchor, Tooltip, Statistic } from 'antd'

const { Link } = Anchor;
import { renderLabel } from '@/utils/renderUtils'
import { SelectC } from '@/i2antdV5'

import { fetchStoreList } from '@/redux/dashboard/storeList/storeSlice'

const breadcrumbList = [
  { breadcrumbName: 'dashboard', href: '/client/commandCenter' },
  { breadcrumbName: '房间管理' }
]

// 样式常量
const STYLES = {
  // 状态颜色
  statusColors: {
    '空闲': '#52c41a',
    '入住': '#1890ff',
    '预订': '#faad14',
    '报损': '#ff4d4f'
  },
  // 统计颜色
  statisticColors: {
    totalRooms: '#1890ff',
    totalBooked: '#faad14',
    totalOccupied: '#1890ff',
    totalVacant: '#52c41a',
    totalDamaged: '#ff4d4f'
  },
  // 间距
  spacing: {
    xs: '4px',
    sm: '6px',
    md: '8px',
    lg: '12px',
    xl: '16px',
    xxl: '20px'
  },
  // Tooltip 配置
  tooltip: {
    width: 700,
    overlayStyle: {
      maxWidth: 'none',
      minWidth: 700
    },
    overlayInnerStyle: {
      maxWidth: 'none',
      width: 700,
      padding: 0,
      backgroundColor: '#f0f0f0',
      borderRadius: 4
    }
  },
  // 房间卡片样式
  roomCard: {
    grid: {
      width: '20%',
      textAlign: 'center',
      padding: '12px 8px',
      boxSizing: 'border-box',
      cursor: 'pointer'
    },
    roomNumber: {
      fontWeight: 'bold',
      marginBottom: '4px'
    },
    roomType: {
      fontSize: '12px',
      color: '#666'
    },
    statusTag: {
      display: 'inline-block',
      padding: '2px 8px',
      borderRadius: '4px',
      fontSize: '12px',
      color: '#fff',
      marginBottom: '8px'
    },
    timeline: {
      display: 'flex',
      justifyContent: 'center',
      gap: '3px',
      flexWrap: 'wrap',
      marginTop: '8px'
    },
    timelineBlock: {
      width: '12px',
      height: '12px',
      borderRadius: '2px',
      cursor: 'pointer'
    },
    timelineHint: {
      fontSize: '12px',
      color: '#999',
      marginTop: '6px'
    }
  },
  // 楼层容器样式
  floorContainer: {
    padding: '20px',
    marginBottom: '20px'
  },
  // 楼层标题样式
  floorTitle: {
    fontSize: '18px',
    fontWeight: 'bold',
    marginBottom: '16px'
  },
  // Tooltip 内容样式
  tooltipContent: {
    container: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: 8,
      width: 700,
      boxSizing: 'border-box',
      padding: '8px'
    },
    dayItem: {
      width: 'calc((100% - 6 * 8px) / 7)',
      textAlign: 'center',
      padding: '6px 4px',
      borderRadius: 4,
      backgroundColor: '#f5f5f5',
      border: '1px solid #e8e8e8',
      boxSizing: 'border-box',
      minWidth: 0,
      overflow: 'hidden'
    },
    statusLabel: {
      display: 'inline-block',
      padding: '2px 6px',
      borderRadius: 4,
      fontSize: 12,
      color: '#fff',
      maxWidth: '100%',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      boxSizing: 'border-box'
    },
    dateLabel: {
      marginTop: 4,
      fontSize: 12,
      color: '#999',
      width: '100%',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      padding: '0 4px',
      boxSizing: 'border-box'
    }
  },
  // 图例样式
  legend: {
    container: {
      marginTop: '16px',
      paddingTop: '16px',
      borderTop: '1px solid #f0f0f0'
    },
    item: {
      display: 'flex',
      alignItems: 'center',
      gap: '4px'
    },
    block: {
      width: '12px',
      height: '12px',
      borderRadius: '2px'
    },
    text: {
      fontSize: '12px',
      color: '#666'
    }
  },
  // 锚点容器样式
  anchorContainer: {
    position: 'sticky',
    top: '100px',
    zIndex: 10,
    padding: '16px'
  }
}

// 生成未来两周的日期数组
const generateFutureDates = () => {
  const dates = [];
  const today = new Date();
  for (let i = 1; i <= 14; i++) {
    const date = new Date(today);
    date.setDate(today.getDate() + i);
    dates.push(date.toISOString().split('T')[0]); // YYYY-MM-DD 格式
  }
  return dates;
};

// 房间状态选项
const roomStatuses = ['空闲', '入住', '预订', '报损'];

// 随机生成房间状态（报损状态概率降低）
const getRandomStatus = () => {
  const random = Math.random();
  // 报损状态只有 5% 的概率
  if (random < 0.05) {
    return '报损';
  }
  // 其他状态：空闲 40%，入住 30%，预订 25%
  const normalStatuses = ['空闲', '入住', '预订'];
  const weightedRandom = Math.random();
  if (weightedRandom < 0.4) {
    return '空闲';
  } else if (weightedRandom < 0.7) {
    return '入住';
  } else {
    return '预订';
  }
};

// 仅用于未来日期的随机状态（不包含“报损”）
const getRandomFutureStatus = () => {
  const weightedRandom = Math.random();
  // 未来日期只会出现：空闲 40%，入住 30%，预订 30%
  if (weightedRandom < 0.4) {
    return '空闲';
  } else if (weightedRandom < 0.7) {
    return '入住';
  } else {
    return '预订';
  }
};

// 随机生成房间类型
const getRandomRoomType = () => {
  const random = Math.random();
  // 多宝房和心宝房各占 50%
  return random < 0.5 ? '多宝房' : '心宝房';
};

// 生成楼层数据
const generateFloorData = (floor) => {
  const rooms = [];
  const futureDates = generateFutureDates();
  
  for (let roomNum = 1; roomNum <= 10; roomNum++) {
    const currentStatus = getRandomStatus();
    const roomType = getRandomRoomType(); // 随机生成房间类型

    const futureStatus = futureDates.map((date, index) => {
      // 第一天一定等于当前状态
      if (index === 0) {
        return {
          date,
          status: currentStatus
        };
      }

      // 报损：只有当天是报损，从第二天开始视为空闲（可再次使用）
      if (currentStatus === '报损') {
        return {
          date,
          status: '空闲'
        };
      }

      // 如果当前状态是 预订 / 入住，则至少连续 7 天相同状态
      if ((currentStatus === '预订' || currentStatus === '入住') && index < 7) {
        return {
          date,
          status: currentStatus
        };
      }

      // 其他天按权重随机（未来日期不再出现“报损”）
      return {
        date,
        status: getRandomFutureStatus()
      };
    });
    
    rooms.push({
      roomNumber: `${floor}${String(roomNum).padStart(2, '0')}`, // 如 201, 202, 301, 3A01
      roomNum, // 房号 1-10
      roomType, // 房间类型：多宝房 或 心宝房
      currentStatus,
      futureStatus
    });
  }
  return rooms;
};

// 模拟数据：包含所有楼层和房间信息
const data = {
  '2': generateFloorData('2'),
  '3': generateFloorData('3'),
  '3A': generateFloorData('3A'),
  '4': generateFloorData('4'),
  '5': generateFloorData('5'),
  '6': generateFloorData('6'),
  '7': generateFloorData('7'),
  '8': generateFloorData('8'),
  '9': generateFloorData('9'),
  '10': generateFloorData('10'),
};
class RoomsManagement extends React.Component {
  constructor(props) {
    super(props);
    this.scrollContainerRef = React.createRef();
  }

  componentDidMount() {
    const { dispatch } = this.props;
    dispatch(fetchStoreList({
      serviceType: 'default',
      dataType: 'storeOption'
    }));
    // 从 URL 获取查询参数
    const query = this.getQuery();
    // 如果需要初始化数据，可以在这里调用
  }

  /**
   * 获取查询参数（排除 dataKey）
   * @returns {Object} 查询参数对象
   */
  getQuery() {
    const { location } = this.props;
    
    if (!location) {
      return {};
    }
    
    // WithRouter 已经将 location.search 解析为对象
    const searchObj = location.search || {};
    
    // 确保 searchObj 是对象类型
    if (typeof searchObj !== 'object' || Array.isArray(searchObj)) {
      return {};
    }
    
    const query = { ...searchObj };
    // 排除 dataKey
    delete query.dataKey;
    
    return query;
  }

  /**
   * 更新 URL 并重新获取数据
   * @param {Object} newQuery - 新的查询对象
   */
  updateUrlAndFetch = (newQuery) => {
    const { location, navigate } = this.props;
    navigate(`${location.pathname}${objToSearch(newQuery)}`, { replace: true });
    
    // 重新获取数据，传入新的 query（排除 dataKey）
    const queryForFetch = { ...newQuery };
    delete queryForFetch.dataKey;
    // 如果需要重新获取数据，可以在这里调用
  };

  /**
   * 更新查询参数
   * @param {string|Object} keyOrUpdates - 查询键或更新对象
   * @param {*} value - 查询值（当 keyOrUpdates 为字符串时）
   */
  updateQuery = (keyOrUpdates, value) => {
    const { location } = this.props;
    const currentSearch = location.search || {};
    const newQuery = { ...currentSearch };
    
    if (typeof keyOrUpdates === 'object') {
      // 批量更新
      Object.keys(keyOrUpdates).forEach((key) => {
        if (
          keyOrUpdates[key] !== undefined &&
          keyOrUpdates[key] !== null &&
          keyOrUpdates[key] !== ''
        ) {
          newQuery[key] = keyOrUpdates[key];
        } else {
          delete newQuery[key];
        }
      });
    } else {
      // 单个更新
      if (value !== undefined && value !== null && value !== '') {
        newQuery[keyOrUpdates] = value;
      } else {
        delete newQuery[keyOrUpdates];
      }
    }
    
    this.updateUrlAndFetch(newQuery);
  };

  /**
   * 计算所有房间的统计数据
   * @returns {Object} 统计数据对象
   */
  getRoomStatistics = () => {
    let totalRooms = 0;
    let totalBooked = 0;
    let totalOccupied = 0;
    let totalVacant = 0;
    let totalDamaged = 0;

    // 遍历所有楼层数据
    Object.keys(data).forEach((floor) => {
      const floorRooms = data[floor] || [];
      floorRooms.forEach((room) => {
        totalRooms++;
        const status = room.currentStatus;
        if (status === '预订') {
          totalBooked++;
        } else if (status === '入住') {
          totalOccupied++;
        } else if (status === '空闲') {
          totalVacant++;
        } else if (status === '报损') {
          totalDamaged++;
        }
      });
    });

    return {
      totalRooms,
      totalBooked,
      totalOccupied,
      totalVacant,
      totalDamaged
    };
  };

  renderRoomsManagement = (floor) => {
    // 从模拟数据中获取该楼层的房间数据
    const floorRooms = data[floor] || [];
    const { statusColors } = STYLES;

    return (
      <div id={`part-${floor}`} style={STYLES.floorContainer}>
        <h2 style={STYLES.floorTitle}>{floor} 楼</h2>
        <Card bordered style={{ width: '100%' }}>
          {floorRooms.map((room) => (
            <Card.Grid
              key={room.roomNumber}
              style={STYLES.roomCard.grid}
              hoverable
            >
              {/* 未来两周状态时间线 + Tooltip 显示详细信息 */}
              <Tooltip
                placement="top"
                overlayStyle={STYLES.tooltip.overlayStyle}
                overlayInnerStyle={STYLES.tooltip.overlayInnerStyle}
                title={
                  room.futureStatus && room.futureStatus.length ? (
                    <div style={STYLES.tooltipContent.container}>
                      {room.futureStatus.map((dayStatus) => (
                        <div key={dayStatus.date} style={STYLES.tooltipContent.dayItem}>
                          <div
                            style={{
                              ...STYLES.tooltipContent.statusLabel,
                              backgroundColor: statusColors[dayStatus.status] || '#999'
                            }}
                          >
                            {dayStatus.status}
                          </div>
                          <div style={STYLES.tooltipContent.dateLabel}>
                            {dayStatus.date}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    '无未来状态数据'
                  )
                }
              >
                <div style={{ marginBottom: STYLES.spacing.md }}>
                  <div style={STYLES.roomCard.roomNumber}>
                    {room.roomNumber}
                  </div>
                  <div style={STYLES.roomCard.roomType}>
                    {room.roomType}
                  </div>
                </div>
                <div
                  style={{
                    ...STYLES.roomCard.statusTag,
                    backgroundColor: statusColors[room.currentStatus] || '#999'
                  }}
                >
                  {room.currentStatus}
                </div>
                
                {/* 未来14天状态时间线 - 简洁显示 */}
                <div style={STYLES.roomCard.timeline}>
                  {room.futureStatus &&
                    room.futureStatus.map((dayStatus, idx) => (
                      <div
                        key={idx}
                        style={{
                          ...STYLES.roomCard.timelineBlock,
                          backgroundColor: statusColors[dayStatus.status] || '#999'
                        }}
                        title={`${dayStatus.date}: ${dayStatus.status}`}
                      />
                    ))}
                </div>
                <div style={STYLES.roomCard.timelineHint}>
                  未来14天（悬停查看详情）
                </div>
              </Tooltip>
            </Card.Grid>
          ))}
        </Card>
      </div>
    )
  }

  render() {
    const { storeObj } = this.props;
    const { default_storeOption: storeOption = [] } = storeObj || {};
    
    // 获取当前查询参数
    const query = this.getQuery() || {};
    const fetching = false; // 如果需要加载状态，可以从 Redux 中获取
    
    // 计算统计数据
    const statistics = this.getRoomStatistics();
    
    return (
      <>
        <div>
          <PageHeader breadcrumbList={breadcrumbList} />
        </div>
        <div className='i2-container'>
          <Card className="i2-card-page">
          <Spin spinning={fetching}>
            <section className="i2-app-toolbar">
              <div className="toolbar-filters">
                <SelectC
                  label=""
                  query={query}
                  searchKey="store_id"  
                  options={storeOption}
                  action={this.updateQuery}
                />
              </div>
            </section>
            <div style={{ marginBottom: STYLES.spacing.xl }}>
              <Card>
                <Row gutter={16} justify="center">
                  <Col span={4}>
                    <Statistic
                      title="总房间数"
                      value={statistics.totalRooms}
                      valueStyle={{ color: STYLES.statisticColors.totalRooms }}
                    />
                  </Col>
                  <Col span={4}>
                    <Statistic
                      title="总预订数"
                      value={statistics.totalBooked}
                      valueStyle={{ color: STYLES.statisticColors.totalBooked }}
                    />
                  </Col>
                  <Col span={4}>
                    <Statistic
                      title="总入住数"
                      value={statistics.totalOccupied}
                      valueStyle={{ color: STYLES.statisticColors.totalOccupied }}
                    />
                  </Col>
                  <Col span={4}>
                    <Statistic
                      title="总空闲数"
                      value={statistics.totalVacant}
                      valueStyle={{ color: STYLES.statisticColors.totalVacant }}
                    />
                  </Col>
                  <Col span={4}>
                    <Statistic
                      title="总报损数"
                      value={statistics.totalDamaged}
                      valueStyle={{ color: STYLES.statisticColors.totalDamaged }}
                    />
                  </Col>
                </Row>
                {/* 状态颜色图例 */}
                <Row gutter={16} justify="center" style={STYLES.legend.container}>
                  <Col>
                    <div style={STYLES.legend.item}>
                      <div style={{ ...STYLES.legend.block, backgroundColor: STYLES.statusColors['空闲'] }}></div>
                      <span style={STYLES.legend.text}>空闲</span>
                    </div>
                  </Col>
                  <Col>
                    <div style={STYLES.legend.item}>
                      <div style={{ ...STYLES.legend.block, backgroundColor: STYLES.statusColors['入住'] }}></div>
                      <span style={STYLES.legend.text}>入住</span>
                    </div>
                  </Col>
                  <Col>
                    <div style={STYLES.legend.item}>
                      <div style={{ ...STYLES.legend.block, backgroundColor: STYLES.statusColors['预订'] }}></div>
                      <span style={STYLES.legend.text}>预订</span>
                    </div>
                  </Col>
                  <Col>
                    <div style={STYLES.legend.item}>
                      <div style={{ ...STYLES.legend.block, backgroundColor: STYLES.statusColors['报损'] }}></div>
                      <span style={STYLES.legend.text}>报损</span>
                    </div>
                  </Col>
                </Row>
              </Card>
            </div>
            <Row>
              <Col span={2}>
                <div style={STYLES.anchorContainer}>
                  <Anchor affix={false} offsetTop={100}>
                    <Link href="#part-2" title="2 楼" />
                    <Link href="#part-3" title="3 楼" />
                    <Link href="#part-3A" title="3A 楼" />
                    <Link href="#part-5" title="5 楼" />
                    <Link href="#part-6" title="6 楼" />
                    <Link href="#part-7" title="7 楼" />
                    <Link href="#part-8" title="8 楼" />
                    <Link href="#part-9" title="9 楼" />
                    <Link href="#part-10" title="10 楼" />
                  </Anchor>
                </div>
              </Col>
              <Col span={20} ref={this.scrollContainerRef}>
                  {this.renderRoomsManagement('2')}
                  {this.renderRoomsManagement('3')}
                  {this.renderRoomsManagement('3A')}
                  {this.renderRoomsManagement('5')}
                  {this.renderRoomsManagement('6')}
                  {this.renderRoomsManagement('7')}
                  {this.renderRoomsManagement('8')}
                  {this.renderRoomsManagement('9')}
                  {this.renderRoomsManagement('10')}
              </Col>
            </Row>
          </Spin>
          </Card>
        </div>
      </>
    )
  }
}

const RoomsManagementWrapper = (props) => {
  const dispatch = useDispatch();
  const allProps = {
    ...props,
    dispatch,
    storeObj: useSelector((state) => state.dashboard.storeList || {}),
    staffListObj: useSelector((state) => state.dashboard.staffList || {}),
  };
  return <RoomsManagement {...allProps} />;
};

export default WithRouter(RoomsManagementWrapper);
